import { useState, useEffect } from 'react';
import { downloadService } from '../services/api';

/**
 * Hook customizado para buscar versões de download
 * Segue princípios SOLID: Single Responsibility
 * 
 * @param {string|number} versionId - ID da versão a buscar (padrão: 'latest')
 * @returns {Object} { data, loading, error, refetch }
 */
export const useDownloadVersions = (versionId = 'VERITUXERP') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Função auxiliar para transformar dados da API
   */
  const transformApiData = (response) => {
    return {
      latest: {
        version: response.latest_version?.version || '',
        url: response.latest_version?.url || '',
        size: formatFileSize(response.latest_version?.size) || '',
        releaseDate: response.latest_version?.release_date || '',
        checksum: response.latest_version?.checksum || ''
      },
      previous: {
        version: response.previous_version?.version || '',
        url: response.previous_version?.url || '',
        size: formatFileSize(response.previous_version?.size) || '',
        releaseDate: response.previous_version?.release_date || '',
        checksum: response.previous_version?.checksum || ''
      }
    };
  };

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await downloadService.getDownloadVersions(versionId);
      const transformedData = transformApiData(response);
      setData(transformedData);
    } catch (err) {
      setError(err);
      console.error('Erro ao carregar versões de download:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  return {
    data,
    loading,
    error,
    refetch: fetchVersions
  };
};

/**
 * Formata tamanho de arquivo em bytes para formato legível
 * @param {number|string} bytes - Tamanho em bytes
 * @returns {string} Tamanho formatado (ex: "125 MB")
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  
  const size = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  
  if (isNaN(size)) return bytes; // Retorna o valor original se não for número
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let fileSize = size;
  
  while (fileSize >= 1024 && unitIndex < units.length - 1) {
    fileSize /= 1024;
    unitIndex++;
  }
  
  return `${fileSize.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};
